/**
 * Component for Titles
 *
 * @module ui/modules/Titles
 * @memberof -Common
 */
import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { TitlesDefault as PalTitlesDefault } from 'web-palette/dist/components/TitlesDefault/TitlesDefault';
import { TitlesHeadline as PalTitlesHeadline } from 'web-palette/dist/components/TitlesHeadline/TitlesHeadline';
import { TitlesPage as PalTitlesPage } from 'web-palette/dist/components/TitlesPage/TitlesPage';

import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { getLinkHtml } from '@ulta/core/utils/getLinkHtml/getLinkHtml';

/**
 * Represents a Titles component
 *
 * @method
 * @param { TitlesProps} props - React properties passed from composition
 * @returns Titles
 */
export const Titles = function( props ){
  const titlesRef = useRef( null );
  const {
    backgroundColor,
    countLabel,
    headline,
    headlineTag = defaultProps.headlineTag,
    linkAction,
    subheadline,
    textAlignment,
    variant = defaultProps.variant
  } = props;

  /** Getting each Titles variant to set which one to display in the return */
  const isTitlesDefault = variant === 'default';
  const isTitlesHeadline = variant === 'headline';
  const isTitlesPage = variant === 'page';

  /** Objects for external media and actions coming from Amplience */
  let formattedAction;
  let formattedPageAction;

  if( linkAction ){
    formattedAction = getLinkHtml( { action: linkAction } );
  }

  if( isTitlesPage && linkAction ){
    formattedPageAction = getLinkHtml( { action: linkAction, iconName: 'ArrowDown', iconPosition: 'after' } );
  }

  useIntersectionProcessor( {
    ...props,
    componentRef: titlesRef
  } );

  return (
    <div className='Titles'
      ref={ titlesRef }
    >
      { isTitlesDefault &&
        <PalTitlesDefault
          externalAction={ formattedAction }
          backgroundColor={ backgroundColor }
          countLabel={ countLabel }
          headline={ headline }
          headlineTag={ headlineTag }
          subheadline={ subheadline }
          textAlignment={ textAlignment }
        />
      }
      { isTitlesHeadline &&
        <PalTitlesHeadline
          backgroundColor={ backgroundColor }
          headline={ headline }
          headlineTag={ headlineTag }
          subheadline={ subheadline }
          textAlignment={ textAlignment }
        />
      }
      { isTitlesPage &&
        <PalTitlesPage
          externalAction={ formattedPageAction }
          headline={ headline }
          headlineTag={ headlineTag }
          linkAction={ linkAction }
          subheadline={ subheadline }
        />
      }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef Titles
 * @type {object}
 * @property {string} backgroundColor - background color of the Titles body content
 * @property {string} countLabel - text/description of the Titles countLabel
 * @property {string} headline - title text of the Titles body
 * @property {string} headlineTag - headline tag of the Titles body
 * @property {object} linkAction - linkAction
 * @property {string} subheadline - Titles subheadline
 * @property {string} textAlignment - Titles textAlignment
 * @property {string} variant - sets the variant of the Titles
 */

export const propTypes = {
  /**
   * background color of the Titles body content
   */
  backgroundColor: PropTypes.string,
  /**
   * text/description of the Titles countLabel
   */
  countLabel: PropTypes.string,
  /**
   * title text of the Titles body
   */
  headline: PropTypes.string,
  /**
   * headline tag of the Titles body
   */
  headlineTag: PropTypes.string,
  /**
   * linkAction
   */
  linkAction: PropTypes.object,
  /**
   * titles subheadline
   */
  subheadline: PropTypes.string,
  /**
   * sets the position of the text
   */
  textAlignment: PropTypes.string,
  /**
   * sets the variant of the Titles
   */
  variant: PropTypes.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} headlineTag - headlineTag: 'h2' - sets the default for the headline tag
 */
export const defaultProps =  {
  headlineTag: 'h2',
  variant: 'default'
};

Titles.propTypes = propTypes;
Titles.defaultProps = defaultProps;

export default Titles;